/**
 * Filter Schemas for Server-Side Filtering
 *
 * Zod schemas shared between frontend and backend for type-safe filtering.
 * These schemas define the structure of filter rules and groups that can be
 * serialized to URL params and passed to tRPC endpoints.
 */

import { z } from "zod";

// ============================================================================
// Filter Operators
// ============================================================================

/**
 * All supported filter operators across different field types
 */
export const filterOperatorSchema = z.enum([
  // Universal operators
  "equals",
  "not_equals",
  "is_empty",
  "is_not_empty",
  // Text operators
  "contains",
  "not_contains",
  "starts_with",
  "ends_with",
  // Numeric operators
  "gt",
  "gte",
  "lt",
  "lte",
  "between",
  // Date operators
  "is_before",
  "is_after",
  "is_on",
  "is_within_last",
  "is_within_next",
  // Enum operators
  "in",
  "not_in",
  // Boolean operators
  "is_true",
  "is_false",
]);

export type FilterOperator = z.infer<typeof filterOperatorSchema>;

// ============================================================================
// Field Types
// ============================================================================

/**
 * Field types determine which operators are available
 */
export const filterFieldTypeSchema = z.enum([
  "text",
  "numeric",
  "date",
  "enum",
  "boolean",
  "user", // Rich user selector with avatars
]);

export type FilterFieldType = z.infer<typeof filterFieldTypeSchema>;

// ============================================================================
// Filter Logic
// ============================================================================

export const filterLogicSchema = z.enum(["AND", "OR"]);

export type FilterLogic = z.infer<typeof filterLogicSchema>;

// ============================================================================
// Filter Value Schemas (Type-Safe by Operator)
// ============================================================================

/**
 * Type-safe filter value schema based on operator type.
 * Each operator category has specific value types:
 * - Text operators: string
 * - Numeric operators: number or [number, number] for between
 * - Date operators: string (ISO date) or string duration (e.g., "7d", "30d")
 * - Enum operators: string[] for in/not_in
 * - Boolean/empty operators: null/undefined (no value needed)
 */
export const filterValueSchema = z.union([
  // String values (text operators, equals, contains, etc.)
  z.string(),
  // Numeric values (gt, lt, gte, lte, equals for numbers)
  z.number(),
  // Array of strings (enum in/not_in operators)
  z.array(z.string()),
  // Tuple of two numbers (between operator)
  z.tuple([z.number(), z.number()]),
  // Null for operators that don't require a value (is_empty, is_true, etc.)
  z.null(),
  // Undefined for operators that don't require a value
  z.undefined(),
]);

export type FilterValue = z.infer<typeof filterValueSchema>;

// ============================================================================
// Filter Rule Schema
// ============================================================================

/**
 * A single filter rule with field, operator, and value.
 * Value is type-safe based on operator category.
 */
export const filterRuleSchema = z.object({
  id: z.string(),
  field: z.string(),
  operator: filterOperatorSchema,
  value: filterValueSchema.optional(),
  fieldType: filterFieldTypeSchema,
});

export type FilterRule = z.infer<typeof filterRuleSchema>;

// ============================================================================
// Filter Group Schema (Recursive)
// ============================================================================

/**
 * Base filter group type for recursive schema
 */
export interface FilterGroup {
  id: string;
  logic: FilterLogic;
  rules: (FilterRule | FilterGroup)[];
}

/**
 * Recursive Zod schema for filter groups
 * Groups can contain rules OR nested groups, enabling complex AND/OR logic
 */
export const filterGroupSchema: z.ZodType<FilterGroup> = z.lazy(() =>
  z.object({
    id: z.string(),
    logic: filterLogicSchema,
    rules: z.array(z.union([filterRuleSchema, filterGroupSchema])),
  }),
);

// ============================================================================
// Type Guards
// ============================================================================

/**
 * Type guard to check if a rule is a FilterGroup (vs FilterRule)
 */
export function isFilterGroup(
  rule: FilterRule | FilterGroup,
): rule is FilterGroup {
  return "logic" in rule && "rules" in rule;
}

/**
 * Type guard to check if a rule is a FilterRule (vs FilterGroup)
 */
export function isFilterRule(
  rule: FilterRule | FilterGroup,
): rule is FilterRule {
  return "field" in rule && "operator" in rule;
}

// ============================================================================
// Operator Metadata
// ============================================================================

/**
 * Operators that don't require a value input
 */
export const OPERATORS_WITHOUT_VALUE: FilterOperator[] = [
  "is_empty",
  "is_not_empty",
  "is_true",
  "is_false",
];

/**
 * Check if an operator requires a value input
 */
export function operatorRequiresValue(operator: FilterOperator): boolean {
  return !OPERATORS_WITHOUT_VALUE.includes(operator);
}

/**
 * Operators available for each field type
 */
export const OPERATORS_BY_FIELD_TYPE: Record<
  FilterFieldType,
  { label: string; value: FilterOperator }[]
> = {
  text: [
    { label: "Contains", value: "contains" },
    { label: "Does not contain", value: "not_contains" },
    { label: "Equals", value: "equals" },
    { label: "Does not equal", value: "not_equals" },
    { label: "Starts with", value: "starts_with" },
    { label: "Ends with", value: "ends_with" },
    { label: "Is empty", value: "is_empty" },
    { label: "Is not empty", value: "is_not_empty" },
  ],
  numeric: [
    { label: "Equals", value: "equals" },
    { label: "Does not equal", value: "not_equals" },
    { label: "Greater than", value: "gt" },
    { label: "Greater than or equal", value: "gte" },
    { label: "Less than", value: "lt" },
    { label: "Less than or equal", value: "lte" },
    { label: "Between", value: "between" },
    { label: "Is empty", value: "is_empty" },
    { label: "Is not empty", value: "is_not_empty" },
  ],
  date: [
    { label: "Is on", value: "is_on" },
    { label: "Is before", value: "is_before" },
    { label: "Is after", value: "is_after" },
    { label: "Is within last", value: "is_within_last" },
    { label: "Is within next", value: "is_within_next" },
    { label: "Is empty", value: "is_empty" },
    { label: "Is not empty", value: "is_not_empty" },
  ],
  enum: [
    { label: "Is", value: "in" },
    { label: "Is not", value: "not_in" },
    { label: "Is empty", value: "is_empty" },
    { label: "Is not empty", value: "is_not_empty" },
  ],
  boolean: [
    { label: "Is true", value: "is_true" },
    { label: "Is false", value: "is_false" },
  ],
  user: [
    { label: "Is", value: "equals" }, // Locked to 'equals' only
  ],
};

/**
 * Get available operators for a given field type
 */
export function getOperatorsForFieldType(
  fieldType: FilterFieldType,
): { label: string; value: FilterOperator }[] {
  return OPERATORS_BY_FIELD_TYPE[fieldType];
}

/**
 * Get default operator for a field type
 */
export function getDefaultOperator(fieldType: FilterFieldType): FilterOperator {
  const operators = OPERATORS_BY_FIELD_TYPE[fieldType];
  const firstOperator = operators[0];
  return firstOperator?.value ?? "equals";
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Generate a unique ID for filters/groups
 */
export function generateFilterId(): string {
  return `filter-${Math.random().toString(36).substring(2, 11)}`;
}

/**
 * Create an empty filter group
 */
export function createEmptyFilterGroup(): FilterGroup {
  return {
    id: generateFilterId(),
    logic: "AND",
    rules: [],
  };
}

/**
 * Create a default filter group excluding TIMED_OUT, RETURNED, and FAILED statuses
 */
export function createDefaultInterviewFilterGroup(): FilterGroup {
  return {
    id: generateFilterId(),
    logic: "AND",
    rules: [
      {
        id: generateFilterId(),
        field: "status",
        operator: "not_in",
        value: ["TIMED_OUT", "RETURNED", "FAILED"],
        fieldType: "enum",
      },
    ],
  };
}

/**
 * Count active filters (excluding empty groups)
 */
export function countActiveFilters(group: FilterGroup): number {
  let count = 0;

  for (const rule of group.rules) {
    if (isFilterGroup(rule)) {
      count += countActiveFilters(rule);
    } else {
      count += 1;
    }
  }

  return count;
}

/**
 * Check if a filter group has any active rules
 */
export function hasActiveFilters(group: FilterGroup): boolean {
  return countActiveFilters(group) > 0;
}

// ============================================================================
// Runtime Type Guards for Filter Values
// ============================================================================

/**
 * Type guard: checks if value is a valid string
 */
export function isStringValue(value: FilterValue): value is string {
  return typeof value === "string";
}

/**
 * Type guard: checks if value is a valid number
 */
export function isNumberValue(value: FilterValue): value is number {
  return typeof value === "number" && !isNaN(value);
}

/**
 * Type guard: checks if value is a valid string array (for in/not_in operators)
 */
export function isStringArrayValue(value: FilterValue): value is string[] {
  return (
    Array.isArray(value) && value.every((item) => typeof item === "string")
  );
}

/**
 * Type guard: checks if value is a valid number tuple (for between operator)
 */
export function isNumberTupleValue(
  value: FilterValue,
): value is [number, number] {
  return (
    Array.isArray(value) &&
    value.length === 2 &&
    typeof value[0] === "number" &&
    typeof value[1] === "number" &&
    !isNaN(value[0]) &&
    !isNaN(value[1])
  );
}

/**
 * Type guard: checks if value is null or undefined (for operators that don't need values)
 */
export function isEmptyValue(value: FilterValue): value is null | undefined {
  return value === null || value === undefined;
}

/**
 * Validates that a filter value matches the expected type for its operator.
 * Returns true if valid, throws descriptive error if invalid.
 */
export function validateFilterValueForOperator(
  operator: FilterOperator,
  value: FilterValue,
  fieldName: string,
): boolean {
  // Operators that don't require a value
  if (OPERATORS_WITHOUT_VALUE.includes(operator)) {
    return true;
  }

  // Text operators require string values
  const textOperators: FilterOperator[] = [
    "contains",
    "not_contains",
    "starts_with",
    "ends_with",
  ];
  if (textOperators.includes(operator)) {
    if (!isStringValue(value)) {
      throw new Error(
        `Filter "${fieldName}" with operator "${operator}" requires a string value, got ${typeof value}`,
      );
    }
    return true;
  }

  // Numeric comparison operators require number values
  const numericOperators: FilterOperator[] = ["gt", "gte", "lt", "lte"];
  if (numericOperators.includes(operator)) {
    if (!isNumberValue(value)) {
      throw new Error(
        `Filter "${fieldName}" with operator "${operator}" requires a number value, got ${typeof value}`,
      );
    }
    return true;
  }

  // Between operator requires a number tuple
  if (operator === "between") {
    if (!isNumberTupleValue(value)) {
      throw new Error(
        `Filter "${fieldName}" with operator "between" requires a [number, number] tuple`,
      );
    }
    return true;
  }

  // In/not_in operators require string array
  if (operator === "in" || operator === "not_in") {
    if (!isStringArrayValue(value)) {
      throw new Error(
        `Filter "${fieldName}" with operator "${operator}" requires a string array`,
      );
    }
    return true;
  }

  // Date operators (is_within_last, is_within_next) require duration string
  const durationOperators: FilterOperator[] = [
    "is_within_last",
    "is_within_next",
  ];
  if (durationOperators.includes(operator)) {
    if (!isStringValue(value)) {
      throw new Error(
        `Filter "${fieldName}" with operator "${operator}" requires a duration string (e.g., "7d", "30d")`,
      );
    }
    return true;
  }

  // equals/not_equals can accept string or number
  if (operator === "equals" || operator === "not_equals") {
    if (!isStringValue(value) && !isNumberValue(value)) {
      throw new Error(
        `Filter "${fieldName}" with operator "${operator}" requires a string or number value`,
      );
    }
    return true;
  }

  // Date comparison operators require string (ISO date)
  const dateOperators: FilterOperator[] = ["is_before", "is_after", "is_on"];
  if (dateOperators.includes(operator)) {
    if (!isStringValue(value)) {
      throw new Error(
        `Filter "${fieldName}" with operator "${operator}" requires an ISO date string`,
      );
    }
    return true;
  }

  return true;
}
